import { AppShell } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { STUDIO_NAVBAR_WIDTH } from "@mountaya/studio-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import { useStore } from "zustand";

import {
  userOrganizationsQueryOptions,
  useUserOrganizations,
  useUserPersonalSpace,
} from "@mountayaapp/api";
import type { IdentityStoreApi } from "@mountayaapp/identity";

import { identifyActiveOrganization, identifyOnce } from "../analytics/rules/identify";
import { safeReturnTo } from "../auth/rules/returnTo";
import { signInIfSignedOut } from "../auth/rules/signedOut";
import { env } from "../env";
import { LazyPaymentMethodModal } from "../iam/billing/components/LazyPaymentMethodModal";
import { summarizeMemberships } from "../iam/shared/rules/organizationResolution";
import { AppNavbar } from "../layout/components/AppNavbar";
import classes from "../layout/components/appShell.module.css";
import { MobileNavDrawer, MobileNavFab, MobileNavProvider } from "../layout/components/MobileNav";
import { SkipLink } from "../layout/components/SkipLink";
import { IAM_NAVBAR_WIDTH, MAIN_CONTENT_ID, NAVBAR_TRANSITION_MS } from "../layout/rules/appShell";
import { resolveNavSection } from "../layout/rules/resolveNavSection";
import { consumeOnboardingIntent } from "../onboarding/rules/intent";
import { useInvalidateMemberships } from "../shared/hooks/useInvalidateMemberships";
import { ElementsProvider } from "../shared/providers/ElementsProvider";
import { personalSpaceReader } from "../shared/rules/personalSpaceReader";
import { CollabProvider } from "../studio/collab/providers/CollabProvider";
import { StudioHost } from "../studio/components/StudioHost";
import { namesForeignScope } from "../studio/rules/foreignScope";
import { useStudioScopeAlignment } from "../studio/shared/hooks/useStudioScopeAlignment";

/**
 * studioSearchSlice parses the open-itinerary id off the loose, tree-wide router
 * search. The studio route owns the authoritative schema; the layout reads the
 * same key here so the StudioProvider load follows the route without prop
 * drilling through the Outlet. Unknown keys (search owned by other routes) are
 * ignored.
 */
const studioSearchSlice = z.object({
  itinerary_id: z.string().optional(),
});

/**
 * Route is the pathless _app layout: the authenticated app shell. Its beforeLoad
 * is the auth guard — anonymous users (no session on the router context, which
 * the root beforeLoad resolved) are redirected to /login with a return_to so
 * they land back here after signing in. Once a session is confirmed, it
 * prefetches the user's organizations through the shared query client and
 * derives org membership into the identity store, so every entitled affordance
 * resolves correctly on the first render. The leading underscore keeps /_app out
 * of the URL; children render inside the <Outlet>.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location, preload }) => {
    if (!context.session) {
      // The return_to is minted through the same validator the guest guard consumes
      // it with, so the two sides of the round trip agree on what a usable
      // destination is and this gate can never hand /login a value that bounces
      // straight back here.
      throw redirect({ search: { return_to: safeReturnTo(location.href) }, to: "/login" });
    }

    // Every new registrant is routed into the create-organization flow on their first
    // authenticated landing. consumeOnboardingIntent is one-shot (it reads and clears), so
    // this fires exactly once; the !preload guard keeps a route preload (a hover prefetch)
    // from burning the intent before the real navigation.
    if (!preload && consumeOnboardingIntent()) {
      throw redirect({ search: { onboarding: true }, to: "/organizations/new" });
    }

    // A failed membership lookup must not blank the authenticated shell. It is not an
    // authorization gate (the session check above is the gate), and the backend enforces
    // every entitlement it feeds, so degrading to no active membership on a lookup failure
    // withholds affordances rather than granting any. The backend answers the whole list 500
    // when one organization's entitlements cannot be read (ADR 0104): a read that failed is not
    // awaited again by every navigation, since the shell's own StudioAnswerReads asks again in
    // the background and settles the memberships once it lands.
    //
    // "Failed" is read off the error count rather than the status: a read asked again with no
    // answer in hand reads `pending` while it is out, and awaiting it would hold a navigation
    // through every retry of the recovery and again at each interval.
    const membershipQueryOptions = userOrganizationsQueryOptions(
      { baseUrl: env.apiBaseUrl },
      context.session.userId,
    );
    const membershipState = context.queryClient.getQueryState(membershipQueryOptions.queryKey);
    const failed =
      membershipState !== undefined &&
      membershipState.data === undefined &&
      membershipState.errorUpdateCount > 0;
    // Null where the read has failed and nothing answered it: the memberships are unknown, which
    // is not the same as none.
    const organizations = failed
      ? null
      : await context.queryClient.ensureQueryData(membershipQueryOptions).catch(() => null);

    // The organizations a person belongs to, and which of them hold Map Studio, are what the
    // studio's entitlements follow from, and this is where they are settled. It is awaited
    // here, before the shell renders, so no surface reads an unsettled answer.
    const memberships = summarizeMemberships(organizations ?? [], env.rootOrganizationId);
    context.identityStore.getState().setMemberships(memberships);

    // A studio URL naming an organization the reader holds no membership in opens on the
    // personal space instead. Settled here, beside the memberships, rather than on the studio's
    // own route: a guard there would run on every write of the studio's search. Only over a read
    // that answered: while the memberships are unknown every organization would read as foreign,
    // and a member opening their own organization during an outage would be sent to the
    // personal space and left there once the read recovers.
    const search: unknown = location.search;
    const scope =
      typeof search === "object" && search !== null && "organization_id" in search
        ? search.organization_id
        : undefined;
    if (
      organizations !== null &&
      location.pathname === "/studio" &&
      namesForeignScope(scope, memberships.organizationIds)
    ) {
      throw redirect({
        replace: true,
        search: (previous) => ({ ...previous, organization_id: undefined }),
        to: "/studio",
      });
    }

    // Identify AFTER setMemberships, never before: until it runs the store reports the
    // reader as belonging to nothing, so a member would be segmented as unaffiliated.
    // identifyOnce carries its own dedupe because this guard re-runs on every navigation;
    // the !preload gate keeps a hover prefetch from identifying on a page the user never
    // opened.
    // The organization group only applies once a route has pinned an active org —
    // the org layout below is what does that on a first landing.
    if (!preload) {
      identifyOnce(context.identityStore);

      const { organizationId } = context.identityStore.getState();
      if (organizationId) {
        identifyActiveOrganization(organizationId, organizations ?? []);
      }
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const { identityStore } = Route.useRouteContext();
  const queryClient = useQueryClient();
  // Once per cache and store, so the viewer the studio host builds over it keeps its identity.
  const personalSpace = useMemo(
    () => personalSpaceReader(queryClient, identityStore),
    [identityStore, queryClient],
  );
  const itineraryId = useRouterState({
    select: (state) => studioSearchSlice.parse(state.location.search).itinerary_id,
  });
  // The studio asks for the memberships to be read again when a co-pilot run or a collaboration
  // session reports that the reader's access moved. Which query holds them is this shell's fact,
  // so the binding is made here and handed to both rather than reached for from `studio/`. The
  // sign-in re-check a co-pilot run or a collaboration session asks for on a 401 is handed on the
  // same way, to both: how a person signs in to this console is the shell's fact too.
  const invalidateMemberships = useInvalidateMemberships();

  // The studio map fills its main region edge-to-edge; everywhere else keeps the
  // standard inset. padding applies to AppShell.Main only, so the navbar is
  // unaffected and non-studio routes stay padded.
  const isStudio = useRouterState({
    select: (state) => state.location.pathname === "/studio",
  });

  // The rail is narrower on the account and organization pages, where it carries a list
  // of settings pages rather than the studio's itinerary tree. It is the same section
  // split the sidenav itself slides on, read from the same resolver, so the rail's width
  // and the panel showing inside it can never answer the route differently.
  const isIam = useRouterState({
    select: (state) => resolveNavSection(state.location.pathname) === "iam",
  });

  // The StudioProvider wraps BOTH the navbar and the routed content so the
  // sidenav itinerary detail and the studio map share one UI store + bus + tool
  // context. It owns the active itinerary (loaded from the route search) and
  // renders the confirm modal; non-studio routes simply render no studio
  // consumer, so the shared surface sits idle for them. AppShell renders in a
  // child component (AppShellBody) inside the provider so the sidenav and studio
  // consumers it wraps resolve the shared UI store, bus, and tool context.
  //
  // CollabProvider sits just inside StudioProvider — above the whole shell — so the
  // real-time collaboration sessions run app-wide (the shared bus/ctx exist here,
  // not just on the studio route) and the live editor session is reachable both by
  // the sidenav roster and by the map editing surfaces. It self-gates on the org's
  // collaboration eligibility, so it sits idle for personal itineraries and
  // non-studio orgs.
  //
  // ElementsProvider sits outermost because the published components are rendered by the
  // shell itself, not only by the studio: a collection row's rename field and the
  // collections menu are `@mountaya/studio-react`, and a component mounted outside the
  // seams draws Lucide glyphs and English copy without failing. The studio mounts a second
  // one further in to add the map facade; a context provider replaces rather than merges, so
  // that one restates these two seams from the same hook.
  return (
    <ElementsProvider>
      <StudioAnswerReads identityStore={identityStore} />
      <StudioHost
        identityStore={identityStore}
        invalidateMemberships={invalidateMemberships}
        itineraryId={itineraryId}
        personalSpace={personalSpace}
        signInIfSignedOut={signInIfSignedOut}
      >
        <CollabProvider
          invalidateMemberships={invalidateMemberships}
          signInIfSignedOut={signInIfSignedOut}
        >
          <AppShellBody isIam={isIam} isStudio={isStudio} />
        </CollabProvider>
      </StudioHost>
    </ElementsProvider>
  );
}

/**
 * StudioAnswerReads keeps the reads of what the platform answers the person may use of the studio
 * (ADR 0104), and renders nothing: a rendered null keeps the reads' re-renders off the shell.
 *
 * The memberships are mirrored into the identity store, where the studio's viewer reads each
 * membership's answer. The route guard settles them before the shell renders, and it degrades to
 * none when the read fails: the backend answers the whole list 500 when one organization's
 * entitlements cannot be read. The shell stays usable meanwhile, offering only what needs no
 * membership, and this asks again (the read's own retries, then every half minute while it stays
 * failed for a reason that passes, and on focus), so the organizations, and what each offers, come
 * back without a navigation. It mirrors every later answer too, an invitation accepted or a role changed, whose
 * writes refresh the read. The mirror is the guard's twin rather than a tool, for the reason the
 * guard writes the store directly: the memberships are server state the viewer reads, and no
 * reader or agent ever sets them.
 *
 * The personal space's answer is kept read into the query cache, which the viewer reads it from
 * (`personalSpaceReader`), and recovers the same way.
 */
function StudioAnswerReads({ identityStore }: { identityStore: IdentityStoreApi }) {
  const userId = useStore(identityStore, (state) => state.userId);
  const { data: organizations } = useUserOrganizations({ baseUrl: env.apiBaseUrl }, userId);
  useUserPersonalSpace({ baseUrl: env.apiBaseUrl }, userId);

  useEffect(() => {
    if (organizations !== undefined) {
      identityStore
        .getState()
        .setMemberships(summarizeMemberships(organizations, env.rootOrganizationId));
    }
  }, [identityStore, organizations]);

  return null;
}

/**
 * AppShellBodyProps carry the studio-route flag that zeroes AppShell.Main's
 * padding so the map fills its region edge-to-edge, and the IAM-route flag that
 * narrows the rail to the documentation site's width.
 */
interface AppShellBodyProps {
  isIam: boolean;
  isStudio: boolean;
}

/**
 * AppShellBody is the shell layout. While an agent run drives the workspace the
 * lock is GRANULAR and lives at the leaf surfaces inside the routed Studio — the
 * itinerary rows, the layers panel, the editor controls, and the back button each
 * read the shared agentDriving flag and lock themselves visibly — rather than an
 * `inert` blanket over the whole app shell. The map stays pannable and the co-pilot
 * chat stays open so the user watches the run and interrupts it with the panel's
 * Stop; AgentDrivingGuard hard-refuses navigation away for the run's duration.
 *
 * It opens on the bypass link, so the first thing a keyboard reaches on any route is a
 * jump past the sidenav to the page's own content rather than the top of a navigation
 * tree the visitor has already read.
 *
 * It also owns the one mobile-navigation disclosure. Below `sm` the desktop rail is
 * collapsed away and the same navigation body is reached from a bottom drawer. Two
 * triggers drive that one drawer: the studio floats its control over the map
 * (bottom-left, clear of the search column), while a settings page renders one in
 * its own flow through the context — so a phone never ends up with navigation it
 * cannot reach.
 */
function AppShellBody({ isIam, isStudio }: AppShellBodyProps) {
  const [navOpen, { close: closeNav, open: openNav }] = useDisclosure(false);
  // Inside the studio host, which holds the open itinerary and zone, and above both the sidenav
  // and the map, which read the scope it aligns.
  useStudioScopeAlignment(isStudio);

  return (
    // Below `sm` the rail is collapsed away entirely rather than left to Mantine's
    // mobile navbar, which renders as a full-width fixed overlay and would bury the
    // map (and every settings page) with no way out. The same navigation is reached
    // from MobileNavDrawer instead.
    //
    // transitionDuration is the panel slide's own duration, so a navigation that crosses
    // between the studio and the IAM pages moves the rail's width, the main region's
    // padding, and the panel travelling inside the rail as one gesture rather than three.
    <AppShell
      navbar={{
        breakpoint: "sm",
        collapsed: { mobile: true },
        width: isIam ? IAM_NAVBAR_WIDTH : STUDIO_NAVBAR_WIDTH,
      }}
      padding={isStudio ? 0 : "md"}
      transitionDuration={NAVBAR_TRANSITION_MS}
    >
      {/* First in the tree so it is the first tab stop: a keyboard visitor reaches the
          page's own content without walking the whole itinerary tree again. */}
      <SkipLink />

      <AppNavbar />

      {/* tabIndex={-1} so following the skip link actually moves focus here. A fragment
          target that is not focusable only scrolls, leaving the next Tab back at the
          top of the navigation the link was meant to bypass. The class carries only the
          reduced-motion guard on the padding Mantine animates as the rail resizes. */}
      <AppShell.Main className={classes.main} id={MAIN_CONTENT_ID} tabIndex={-1}>
        <MobileNavProvider onOpen={openNav}>
          <Outlet />
        </MobileNavProvider>
      </AppShell.Main>

      {/* The mobile navigation: one drawer for every authenticated route, plus the
          studio's own floating trigger over the map. A settings page renders its
          trigger inline instead (IamPageContainer), driving this same drawer. */}
      <MobileNavDrawer onClose={closeNav} opened={navOpen} />
      {isStudio ? <MobileNavFab onClick={openNav} /> : null}

      {/* The inline payment dialog is mounted once for the whole authenticated app:
          any billable action refused for lack of a card on file opens it through the
          card-gate store, regardless of the current route. The lazy wrapper defers the
          Stripe.js chunk until the gate first opens, so users who never hit a billable
          action never download it. */}
      <LazyPaymentMethodModal />
    </AppShell>
  );
}
